import React, { useState } from 'react';
import { Table, Search, Loader } from 'lucide-react';

import { getBQTableSchema } from 'services/apiService'

interface TableSelectorProps {
  onTableSelected: (config: BigQueryConfig, schema: TableSchema) => void;
  firestoreProjectId: string;
  existingConfig?: BigQueryConfig | null;
}

interface BigQueryConfig {
  project_id: string;
  dataset_id: string;
  table_name: string;
}

interface TableSchema {
  columns: Array<{
    name: string;
    type: string;
  }>;
}

const TableSelector: React.FC<TableSelectorProps> = ({ onTableSelected, firestoreProjectId, existingConfig }) => {
  const [projectId, setProjectId] = useState(existingConfig?.project_id || '');
  const [datasetId, setDatasetId] = useState(existingConfig?.dataset_id || '');
  const [tableName, setTableName] = useState(existingConfig?.table_name || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFetchSchema = async () => {
    if (!projectId || !datasetId || !tableName) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getBQTableSchema(projectId, datasetId, tableName, firestoreProjectId);
      
      const config: BigQueryConfig = {
        project_id: projectId,
        dataset_id: datasetId,
        table_name: tableName
      };

      const schema: TableSchema = {
        columns: data.schema || []
      };

      onTableSelected(config, schema);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Table className="w-6 h-6 text-blue-400" />
        <h2 className="text-xl font-semibold text-white">Select BigQuery Table</h2>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Project ID
          </label>
          <input
            type="text"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            placeholder="e.g., my_project"
            className="w-full bg-gray-900/50 border border-gray-700 text-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Dataset ID
          </label>
          <input
            type="text"
            value={datasetId}
            onChange={(e) => setDatasetId(e.target.value)}
            placeholder="e.g., my_dataset"
            className="w-full bg-gray-900/50 border border-gray-700 text-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Table Name
          </label>
          <input
            type="text"
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            placeholder="e.g., products"
            className="w-full bg-gray-900/50 border border-gray-700 text-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition"
          />
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <button
          onClick={handleFetchSchema}
          disabled={loading || !projectId || !datasetId || !tableName}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98] disabled:from-gray-700 disabled:to-gray-600 disabled:shadow-none disabled:cursor-not-allowed disabled:scale-100 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Loading Schema...
            </>
          ) : (
            <>
              <Search className="w-5 h-5" />
              Fetch Table Schema
            </>
          )}
        </button>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mt-6">
        <p className="text-sm text-blue-300">
          <strong>Tip:</strong> Make sure you have the necessary permissions to access the BigQuery table (BigQuery Data Viewer role for <strong>bulk-treatment@arcane-ai.iam.gserviceaccount.com</strong>).
          The backend will fetch the schema and first few rows for preview.
        </p>
      </div>
    </div>
  );
};

export default TableSelector;
